import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Shield, Upload, Download } from 'lucide-react';
import { mastersApi } from '../services/api';
import { Table, Pagination, Modal, Field, Spinner } from '../components/common';
import { fmt, StatusBadge } from '../utils/helpers';
import toast from 'react-hot-toast';

// ─── Generic Master List factory ──────────────────────────
function MasterList({ title, sub, columns, fetchFn, FormComponent, formSize='md', extraActions }) {
  const [data,    setData]    = useState({ data:[], pagination:{} });
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);
  const [search,  setSearch]  = useState('');
  const [showForm,setShowForm]= useState(false);
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchFn({ page, limit:50, search: search || undefined });
      setData(r);
    } catch {} finally { setLoading(false); }
  }, [page, search, fetchFn]);

  useEffect(() => { load(); }, [load]);

  const openEdit = row => { setEditRow(row); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditRow(null); };

  const allCols = [
    ...columns,
    { key:'is_active', title:'Status', render: v => <span className={`badge ${v?'badge-green':'badge-red'}`}>{v?'Active':'Inactive'}</span> },
    { key:'id', title:'', align:'right', render:(_,r)=>(
      <button className="btn btn-ghost btn-sm btn-icon" onClick={()=>openEdit(r)}><Edit2 size={13}/></button>
    )},
  ];

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">{title}</h1>{sub&&<p className="page-sub">{sub}</p>}</div>
        <div className="page-actions">
          {extraActions}
          <button className="btn btn-primary" onClick={()=>setShowForm(true)}><Plus size={14}/> Add {title.replace(/s$/,'')}</button>
        </div>
      </div>
      <div className="card">
        <div style={{padding:'12px 20px',borderBottom:'1px solid var(--border-light)'}}>
          <input className="search-input" style={{maxWidth:260}} placeholder={`Search ${title.toLowerCase()}…`} value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} />
        </div>
        <Table columns={allCols} data={data.data} loading={loading} />
        {data.pagination?.total > 0 && <Pagination page={page} totalPages={data.pagination?.totalPages||1} total={data.pagination?.total||0} limit={50} onPage={setPage}/>}
      </div>
      {FormComponent && (
        <FormComponent open={showForm} onClose={closeForm} editData={editRow} onSuccess={()=>{ closeForm(); load(); }} />
      )}
    </div>
  );
}

// ─── Divisions ────────────────────────────────────────────
export function Divisions() {
  const cols = [
    { key:'name', title:'Division Name', render:v=><span style={{fontWeight:500}}>{v}</span> },
    { key:'code', title:'Code',          render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)',background:'var(--bg)',padding:'2px 6px',borderRadius:4}}>{v}</span> },
    { key:'head_name', title:'Head' },
    { key:'created_at', title:'Created', render:v=>fmt.date(v) },
  ];
  return <MasterList title="Divisions" sub="Organisational divisions" columns={cols}
    fetchFn={mastersApi.divisions} FormComponent={DivisionForm} />;
}
function DivisionForm({ open, onClose, editData, onSuccess }) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => { setForm(editData || {}); }, [editData]);
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));
  const save = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setLoading(true);
    try {
      editData ? await mastersApi.updateDiv(editData.id, form) : await mastersApi.createDiv(form);
      toast.success(editData?'Division updated':'Division created');
      onSuccess();
    } catch {} finally { setLoading(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={editData?'Edit Division':'Add Division'} size="sm"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading?<Spinner size="sm"/>:'Save'}</button></>}>
      <Field label="Name" required><input className="form-control" value={form.name||''} onChange={set('name')} /></Field>
      <Field label="Code"><input className="form-control" value={form.code||''} onChange={set('code')} style={{textTransform: form.code ? 'uppercase' : 'none'}} placeholder={editData ? '' : 'Optional'} disabled={!!editData} /></Field>
      <Field label="Head / Manager"><input className="form-control" value={form.head_name||''} onChange={set('head_name')} /></Field>
    </Modal>
  );
}

// ─── Departments ──────────────────────────────────────────
export function Departments() {
  const cols = [
    { key:'name', title:'Department', render:v=><span style={{fontWeight:500}}>{v}</span> },
    { key:'code', title:'Code', render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)',background:'var(--bg)',padding:'2px 6px',borderRadius:4}}>{v}</span> },
    { key:'division_name', title:'Division', render:v=><span style={{fontSize:'var(--fs-xs)',color:'var(--text-secondary)'}}>{v||'—'}</span> },
    { key:'head_name', title:'Head' },
  ];
  return <MasterList title="Departments" columns={cols} fetchFn={mastersApi.departments} FormComponent={DepartmentForm} />;
}
function DepartmentForm({ open, onClose, editData, onSuccess }) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [divisions, setDivisions] = useState([]);
  useEffect(() => { setForm(editData||{}); mastersApi.divisions({limit:200}).then(r=>setDivisions(r.data||[])); }, [editData]);
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));
  const save = async () => {
    if (!form.division_id) { toast.error('Division is required'); return; }
    if (!form.name) { toast.error('Name is required'); return; }
    setLoading(true);
    try {
      editData ? await mastersApi.updateDept(editData.id, form) : await mastersApi.createDept(form);
      toast.success('Saved'); onSuccess();
    } catch {} finally { setLoading(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={editData?'Edit Department':'Add Department'} size="sm"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading?<Spinner size="sm"/>:'Save'}</button></>}>
      <Field label="Division" required><select className="form-control" value={form.division_id||''} onChange={set('division_id')}><option value="">Select…</option>{divisions.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}</select></Field>
      <Field label="Name" required><input className="form-control" value={form.name||''} onChange={set('name')} /></Field>
      <Field label="Code"><input className="form-control" value={form.code||''} onChange={set('code')} style={{textTransform: form.code ? 'uppercase' : 'none'}} placeholder={editData ? '' : 'Optional'} disabled={!!editData} /></Field>
      <Field label="Head"><input className="form-control" value={form.head_name||''} onChange={set('head_name')} /></Field>
    </Modal>
  );
}

// ─── Locations ────────────────────────────────────────────
export function Locations() {
  const cols = [
    { key:'name', title:'Location', render:v=><span style={{fontWeight:500}}>{v}</span> },
    { key:'code', title:'Code',     render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)',background:'var(--bg)',padding:'2px 6px',borderRadius:4}}>{v}</span> },
    { key:'parent_name', title:'Parent', render:v=><span style={{fontSize:'var(--fs-xs)',color:'var(--text-secondary)'}}>{v||'—'}</span> },
    { key:'level_type', title:'Level', render:v=><span className="badge badge-gray" style={{textTransform:'capitalize'}}>{v}</span> },
    { key:'asset_count', title:'Assets', align:'right', render:v=>fmt.number(v||0) },
  ];
  return <MasterList title="Locations" sub="Hierarchical location master (Building → Floor → Room → Rack)" columns={cols} fetchFn={mastersApi.locations} FormComponent={LocationForm} />;
}
function LocationForm({ open, onClose, editData, onSuccess }) {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [locs, setLocs] = useState([]);
  useEffect(() => { setForm(editData||{level_type:'room'}); mastersApi.locations({limit:500}).then(r=>setLocs(r.data||[])); }, [editData]);
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));
  const save = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setLoading(true);
    try {
      editData ? await mastersApi.updateLoc(editData.id, form) : await mastersApi.createLoc(form);
      toast.success('Saved'); onSuccess();
    } catch {} finally { setLoading(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={editData?'Edit Location':'Add Location'} size="sm"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading?<Spinner size="sm"/>:'Save'}</button></>}>
      <div className="form-row cols-2">
        <Field label="Name" required><input className="form-control" value={form.name||''} onChange={set('name')} /></Field>
        <Field label="Code"><input className="form-control" value={form.code||''} onChange={set('code')} style={{textTransform: form.code ? 'uppercase' : 'none'}} placeholder={editData ? '' : 'Optional'} /></Field>
      </div>
      <div className="form-row cols-2">
        <Field label="Level Type"><select className="form-control" value={form.level_type||'room'} onChange={set('level_type')}><option value="building">Building</option><option value="floor">Floor</option><option value="room">Room</option><option value="rack">Rack</option><option value="outdoor">Outdoor</option></select></Field>
        <Field label="Parent Location"><select className="form-control" value={form.parent_id||''} onChange={set('parent_id')}><option value="">None (top level)</option>{locs.filter(l=>l.id!==editData?.id).map(l=><option key={l.id} value={l.id}>{l.name}</option>)}</select></Field>
      </div>
      <Field label="Address"><input className="form-control" value={form.address||''} onChange={set('address')} /></Field>
    </Modal>
  );
}

// ─── Categories ───────────────────────────────────────────
export function Categories() {
  const [tab, setTab] = useState('major');
  const majCols = [
    { key:'name', title:'Category',    render:v=><span style={{fontWeight:500}}>{v}</span> },
    { key:'code', title:'Code',        render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)',background:'var(--bg)',padding:'2px 6px',borderRadius:4}}>{v}</span> },
    { key:'default_dep_method', title:'Method',  render:v=><span className="badge badge-blue">{v}</span> },
    { key:'useful_life_months', title:'Life',    render:v=>`${v} mo` },
    { key:'salvage_pct',        title:'Salvage', render:v=>`${v}%` },
    { key:'minor_count',        title:'Sub-cats',align:'right',render:v=>fmt.number(v||0) },
    { key:'asset_count',        title:'Assets',  align:'right',render:v=>fmt.number(v||0) },
  ];
  const minCols = [
    { key:'name',                title:'Sub-Category', render:v=><span style={{fontWeight:500}}>{v}</span> },
    { key:'code',                title:'Code',         render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)',background:'var(--bg)',padding:'2px 6px',borderRadius:4}}>{v}</span> },
    { key:'major_category_name', title:'Major Category' },
    { key:'default_dep_method',  title:'Method Override', render:v=>v?<span className="badge badge-teal">{v}</span>:<span style={{color:'var(--text-muted)'}}>From parent</span> },
  ];
  return (
    <div>
      <div className="page-header"><div><h1 className="page-title">Asset Categories</h1><p className="page-sub">Major and minor category configuration with depreciation rates</p></div></div>
      <div className="tabs">
        <button className={`tab-btn${tab==='major'?' active':''}`} onClick={()=>setTab('major')}>Major Categories</button>
        <button className={`tab-btn${tab==='minor'?' active':''}`} onClick={()=>setTab('minor')}>Minor Categories</button>
      </div>
      {tab==='major' && <MasterList key="maj" title="Major Categories" columns={majCols} fetchFn={mastersApi.majorCategories} FormComponent={MajorCatForm} />}
      {tab==='minor' && <MasterList key="min" title="Minor Categories" columns={minCols} fetchFn={mastersApi.minorCategories} FormComponent={MinorCatForm} />}
    </div>
  );
}
function MajorCatForm({ open, onClose, editData, onSuccess }) {
  const [form, setForm] = useState({default_dep_method:'SLM',useful_life_months:60,salvage_pct:5});
  const [loading, setLoading] = useState(false);
  useEffect(() => { setForm(editData||{default_dep_method:'SLM',useful_life_months:60,salvage_pct:5}); }, [editData]);
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));
  const save = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setLoading(true);
    try {
      editData ? await mastersApi.updateMajorCat(editData.id,form) : await mastersApi.createMajorCat(form);
      toast.success('Saved'); onSuccess();
    } catch {} finally { setLoading(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={editData?'Edit Category':'Add Major Category'} size="md"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading?<Spinner size="sm"/>:'Save'}</button></>}>
      <div className="form-row cols-2">
        <Field label="Name" required><input className="form-control" value={form.name||''} onChange={set('name')} /></Field>
        <Field label="Code"><input className="form-control" value={form.code||''} onChange={set('code')} style={{textTransform: form.code ? 'uppercase' : 'none'}} placeholder={editData ? '' : 'Optional'} disabled={!!editData} /></Field>
      </div>
      <div className="form-row cols-3">
        <Field label="Dep. Method"><select className="form-control" value={form.default_dep_method||'SLM'} onChange={set('default_dep_method')}><option value="SLM">SLM</option><option value="WDV">WDV</option></select></Field>
        <Field label="Useful Life (mo)"><input className="form-control" type="number" value={form.useful_life_months||''} onChange={set('useful_life_months')} /></Field>
        <Field label="Salvage %"><input className="form-control" type="number" value={form.salvage_pct||''} onChange={set('salvage_pct')} /></Field>
      </div>
      <p style={{fontSize:'var(--fs-xs)',fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'.5px',margin:'16px 0 10px'}}>Depreciation Rates</p>
      <div className="form-row cols-2">
        <Field label="SLM Rate — Companies Act %"><input className="form-control" type="number" step="0.01" value={form.slm_rate_companies||''} onChange={set('slm_rate_companies')} /></Field>
        <Field label="WDV Rate — Companies Act %"><input className="form-control" type="number" step="0.01" value={form.wdv_rate_companies||''} onChange={set('wdv_rate_companies')} /></Field>
        <Field label="SLM Rate — IT Act %"><input className="form-control" type="number" step="0.01" value={form.slm_rate_it||''} onChange={set('slm_rate_it')} /></Field>
        <Field label="WDV Rate — IT Act %"><input className="form-control" type="number" step="0.01" value={form.wdv_rate_it||''} onChange={set('wdv_rate_it')} /></Field>
      </div>
    </Modal>
  );
}
function MinorCatForm({ open, onClose, editData, onSuccess }) {
  const [form, setForm]   = useState({});
  const [loading, setLoading] = useState(false);
  const [majCats, setMajCats] = useState([]);
  useEffect(() => { setForm(editData||{}); mastersApi.majorCategories({limit:200}).then(r=>setMajCats(r.data||[])); }, [editData]);
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));
  const save = async () => {
    if (!form.name||!form.major_category_id) { toast.error('Major Category and Name are required'); return; }
    setLoading(true);
    try {
      editData ? await mastersApi.updateMinorCat(editData.id,form) : await mastersApi.createMinorCat(form);
      toast.success('Saved'); onSuccess();
    } catch {} finally { setLoading(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={editData?'Edit Minor Category':'Add Minor Category'} size="sm"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading?<Spinner size="sm"/>:'Save'}</button></>}>
      <Field label="Major Category" required><select className="form-control" value={form.major_category_id||''} onChange={set('major_category_id')}><option value="">Select…</option>{majCats.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>
      <div className="form-row cols-2">
        <Field label="Name" required><input className="form-control" value={form.name||''} onChange={set('name')} /></Field>
        <Field label="Code"><input className="form-control" value={form.code||''} onChange={set('code')} style={{textTransform: form.code ? 'uppercase' : 'none'}} placeholder={editData ? '' : 'Optional'} disabled={!!editData} /></Field>
      </div>
    </Modal>
  );
}

// ─── Vendors ──────────────────────────────────────────────
export function Vendors() {
  const cols = [
    { key:'name', title:'Vendor Name', render:v=><span style={{fontWeight:500}}>{v}</span> },
    { key:'code', title:'Code',        render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)'}}>{v}</span> },
    { key:'vendor_type', title:'Type', render:v=><span className="badge badge-gray" style={{textTransform:'capitalize'}}>{v?.replace(/_/g,' ')}</span> },
    { key:'contact_person', title:'Contact' },
    { key:'phone',          title:'Phone',   render:v=><span style={{fontSize:'var(--fs-xs)'}}>{v||'—'}</span> },
    { key:'gstin',          title:'GSTIN',   render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)'}}>{v||'—'}</span> },
  ];
  return <MasterList title="Vendors" sub="Supplier and AMC vendor master" columns={cols} fetchFn={mastersApi.vendors} FormComponent={VendorForm} />;
}
function VendorForm({ open, onClose, editData, onSuccess }) {
  const [form, setForm]   = useState({vendor_type:'supplier'});
  const [loading, setLoading] = useState(false);
  useEffect(() => { setForm(editData||{vendor_type:'supplier'}); }, [editData]);
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));
  const save = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setLoading(true);
    try {
      editData ? await mastersApi.updateVendor(editData.id,form) : await mastersApi.createVendor(form);
      toast.success('Saved'); onSuccess();
    } catch {} finally { setLoading(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={editData?'Edit Vendor':'Add Vendor'} size="md"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading?<Spinner size="sm"/>:'Save'}</button></>}>
      <div className="form-row cols-2">
        <Field label="Name" required><input className="form-control" value={form.name||''} onChange={set('name')} /></Field>
        <Field label="Code"><input className="form-control" value={form.code||''} onChange={set('code')} style={{textTransform: form.code ? 'uppercase' : 'none'}} placeholder={editData ? '' : 'Optional'} disabled={!!editData} /></Field>
      </div>
      <div className="form-row cols-2">
        <Field label="Type"><select className="form-control" value={form.vendor_type||'supplier'} onChange={set('vendor_type')}><option value="supplier">Supplier</option><option value="amc_vendor">AMC Vendor</option><option value="both">Both</option></select></Field>
        <Field label="Contact Person"><input className="form-control" value={form.contact_person||''} onChange={set('contact_person')} /></Field>
      </div>
      <div className="form-row cols-2">
        <Field label="Email"><input className="form-control" type="email" value={form.email||''} onChange={set('email')} /></Field>
        <Field label="Phone"><input className="form-control" value={form.phone||''} onChange={set('phone')} /></Field>
      </div>
      <div className="form-row cols-2">
        <Field label="GSTIN"><input className="form-control" value={form.gstin||''} onChange={set('gstin')} /></Field>
        <Field label="State"><input className="form-control" value={form.state||''} onChange={set('state')} /></Field>
      </div>
      <Field label="Address"><textarea className="form-control" rows={2} value={form.address||''} onChange={set('address')} /></Field>
    </Modal>
  );
}

// ─── Users ────────────────────────────────────────────────
import BulkUploadModal from '../components/common/BulkUploadModal';
export function Users() {
  const cols = [
    { key:'full_name',  title:'Name',       render:v=><span style={{fontWeight:500}}>{v}</span> },
    { key:'email',      title:'Email',       render:v=><span style={{fontSize:'var(--fs-xs)'}}>{v}</span> },
    { key:'designation',title:'Designation', render:v=><span style={{fontSize:'var(--fs-xs)',color:'var(--text-secondary)'}}>{v||'—'}</span> },
    { key:'roles',      title:'Roles',       render:v=><>{(v||[]).filter(Boolean).map(r=><span key={r.id} className="badge badge-blue" style={{marginRight:4}}>{r.name}</span>)}</> },
    { key:'department_name', title:'Department' },
    { key:'last_login', title:'Last Login',  render:v=>fmt.datetime(v) },
    { key:'is_active',  title:'Status',      render:v=><span className={`badge ${v?'badge-green':'badge-red'}`}>{v?'Active':'Inactive'}</span> },
  ];
  return <MasterList title="Users" sub="System users and access management" columns={cols} fetchFn={mastersApi.users} FormComponent={UserForm} />;
}
function UserForm({ open, onClose, editData, onSuccess }) {
  const [form, setForm]   = useState({});
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [tempPwd, setTempPwd] = useState(null);
  useEffect(() => {
    setForm(editData||{});
    setSelectedRoles(editData?.role_ids || editData?.roles?.map(r=>r.id) || []);
    setTempPwd(null);
    mastersApi.roles().then(r=>setRoles(r.data||[]));
  }, [editData]);
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));
  const toggleRole = id => setSelectedRoles(r => r.includes(id) ? r.filter(x=>x!==id) : [...r,id]);
  const save = async () => {
    if (!form.email||!form.full_name) { toast.error('Email and name required'); return; }
    setLoading(true);
    try {
      const payload = { ...form, role_ids: selectedRoles };
      if (editData) {
        await mastersApi.updateUser(editData.id,payload);
        toast.success('Saved'); onSuccess();
      } else {
        const r = await mastersApi.createUser(payload);
        // No password field exists in this form by design (the account
        // holder shouldn't need someone else to hand-pick their password)
        // — the backend auto-generates one and emails it. Surfacing it
        // here too, since mail delivery may not be configured in every
        // environment and there'd otherwise be no way to hand it over.
        if (r.data?.temp_password) { setTempPwd(r.data.temp_password); toast.success('User created'); }
        else { toast.success('Saved'); onSuccess(); }
      }
    } catch {} finally { setLoading(false); }
  };

  if (tempPwd) return (
    <Modal open={open} onClose={()=>{setTempPwd(null);onSuccess();}} title="User Created" size="sm"
      footer={<button className="btn btn-primary w-full" onClick={()=>{setTempPwd(null);onSuccess();}}>Done</button>}>
      <div style={{background:'var(--accent-light)',borderRadius:'var(--r-md)',padding:16,textAlign:'center',marginBottom:16}}>
        <p style={{fontWeight:600,fontSize:'var(--fs-md)'}}>{form.full_name} can now sign in</p>
        <p style={{fontSize:'var(--fs-xs)',color:'var(--text-muted)',marginTop:4}}>A welcome email was sent to {form.email} — share this temporary password too in case delivery didn't go through. They'll be asked to change it on first login.</p>
      </div>
      <div style={{display:'flex',justifyContent:'space-between',padding:'10px 0',borderTop:'1px solid var(--border-light)',borderBottom:'1px solid var(--border-light)',fontSize:'var(--fs-sm)'}}>
        <span style={{color:'var(--text-muted)'}}>Temp Password</span>
        <span style={{fontFamily:'monospace',fontWeight:600}}>{tempPwd}</span>
      </div>
    </Modal>
  );

  return (
    <Modal open={open} onClose={onClose} title={editData?'Edit User':'Add User'} size="md"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading?<Spinner size="sm"/>:'Save'}</button></>}>
      <div className="form-row cols-2">
        <Field label="Full Name" required><input className="form-control" value={form.full_name||''} onChange={set('full_name')} /></Field>
        <Field label="Email" required><input className="form-control" type="email" value={form.email||''} onChange={set('email')} disabled={!!editData} /></Field>
      </div>
      <div className="form-row cols-2">
        <Field label="Employee ID"><input className="form-control" value={form.employee_id||''} onChange={set('employee_id')} /></Field>
        <Field label="Phone"><input className="form-control" value={form.phone||''} onChange={set('phone')} /></Field>
      </div>
      <Field label="Designation"><input className="form-control" value={form.designation||''} onChange={set('designation')} /></Field>
      <Field label="Assign Roles">
        <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:4}}>
          {roles.map(r=>(
            <label key={r.id} style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:'var(--fs-sm)',padding:'5px 10px',borderRadius:'var(--r-sm)',border:'1px solid var(--border)',background:selectedRoles.includes(r.id)?'var(--primary-light)':'var(--white)'}}>
              <input type="checkbox" checked={selectedRoles.includes(r.id)} onChange={()=>toggleRole(r.id)} />
              <Shield size={11}/> {r.name}
            </label>
          ))}
        </div>
      </Field>
    </Modal>
  );
}

export { MasterList };
